import React, { useState, useEffect } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  Button,
  Typography,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  ListItemText,
  TextField,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { BACKEND_BASE_URL } from "../config";
import api from "../components/api";

export default function EditNotification({ notification = {}, onSave }) {
  const [formData, setFormData] = useState({
    type: "Email",
    context: "",
    subject: "", // Add subject here
    recipients: [],
    schedule: null,
  });

  const [users, setUsers] = useState([]);
  const typeOptions = ["Email", "WhatsApp", "Both"];

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const token = localStorage.getItem("authToken");
        const response = await api.get(`${BACKEND_BASE_URL}/api/admin-users/`);
        setUsers(response.data.results || []);
      } catch (error) {
        console.error("Error fetching users:", error);
        toast.error("Failed to fetch users. Please try again.");
      }
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    if (notification) {
      setFormData({
        type: notification.type || "Email",
        context: notification.context || "",
        subject: notification.subject || "", // Prepopulate subject
        recipients: Array.isArray(notification.recipients) ? notification.recipients : [],
        schedule: notification.schedule ? new Date(notification.schedule) : null,
      });
    }
  }, [notification]);

  const handleRecipientsChange = (event) => {
    const value = event.target.value;

    if (value.includes("all")) {
      setFormData((prev) => ({ ...prev, recipients: ["all"] }));
    } else {
      setFormData((prev) => ({
        ...prev,
        recipients: value.filter((v) => v !== "all"),
      }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleQuillChange = (value) => {
    setFormData((prev) => ({ ...prev, context: value }));
  };

  const handleScheduleChange = (newValue) => {
    if (newValue) {
      const now = new Date();
      if (newValue.getTime() < now.getTime()) {
        toast.error("Schedule date and time cannot be in the past.");
        return;
      }
      setFormData((prev) => ({ ...prev, schedule: newValue.toISOString() }));
    } else {
      setFormData((prev) => ({ ...prev, schedule: null }));
    }
  };

  const getRecipientLabel = (user) => {
    if (!user) return "Unknown User";
    if (formData.type === "Email") return `${user.name} (${user.email || "N/A"})`;
    if (formData.type === "WhatsApp") return `${user.name} (${user.phone_number || "N/A"})`;
    if (formData.type === "Both") return `${user.name} (${user.email || "N/A"})`;
    return user.name;
  };

  const handleSubmit = () => {
    const { type, context, subject, recipients, schedule } = formData;

    if (!type || !context || !subject || !recipients.length || !schedule) {
      toast.error("Please fill in all required fields.");
      return;
    }
    const mappedType =
      type === "Email" ? "email" : type === "Whatsapp" ? "whatsapp" : "both";
    onSave({ ...formData, type: mappedType, id: notification.id });
  };

  const styles = {
    container: {
      maxWidth: "600px",
      margin: "20px auto",
      padding: "20px",
      border: "1px solid #ddd",
      borderRadius: "8px",
      backgroundColor: "#f9f9f9",
      boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
    },
    header: { marginBottom: "20px" },
    submitButton: { marginTop: "20px", width: "100%" },
  };

  return (
    <div style={styles.container}>
      <Typography variant="h4" style={styles.header}>
        Edit Notification
      </Typography>

      <TextField
        label="Subject *"
        name="subject"
        value={formData.subject}
        onChange={handleChange}
        fullWidth
        margin="normal"
      />

      <FormControl fullWidth margin="normal">
        <InputLabel>Type *</InputLabel>
        <Select
          name="type"
          value={formData.type}
          onChange={handleChange}
          label="Type"
        >
          {typeOptions.map((type) => (
            <MenuItem key={type} value={type}>
              {type}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Typography variant="h6" gutterBottom>
        Context *
      </Typography>
      <ReactQuill
        value={formData.context}
        onChange={handleQuillChange}
        theme="snow"
        style={{ minHeight: "150px", marginBottom: "20px" }}
      />

      <FormControl fullWidth margin="normal">
        <InputLabel>Recipients *</InputLabel>
        <Select
          multiple
          value={formData.recipients}
          onChange={handleRecipientsChange}
          renderValue={(selected) =>
            selected.includes("all")
              ? "All Users"
              : selected
                  .map((id) => {
                    const user = users.find((u) => u.id === id);
                    return user ? user.name : null;
                  })
                  .filter(Boolean)
                  .join(", ")
          }
        >
          <MenuItem value="all">
            <Checkbox checked={formData.recipients.includes("all")} />
            <ListItemText primary="All Users" />
          </MenuItem>
          {users.map((user) => (
            <MenuItem key={user.id} value={user.id} disabled={formData.recipients.includes("all")}>
              <Checkbox checked={formData.recipients.includes(user.id)} />
              <ListItemText primary={getRecipientLabel(user)} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DateTimePicker
          label="Schedule *"
          value={formData.schedule}
          onChange={handleScheduleChange}
          disablePast
          renderInput={(params) => <TextField {...params} fullWidth margin="normal" />}
        />
      </LocalizationProvider>

      <Button
        variant="contained"
        color="primary"
        onClick={handleSubmit}
        style={styles.submitButton}
      >
        Save Changes
      </Button>
    </div>
  );
}





// import React, { useState, useEffect } from 'react';
// import { TextField, Button, MenuItem, FormControl, InputLabel, Select, Checkbox, ListItemText, FormControlLabel, RadioGroup, Radio } from '@mui/material';
// import axios from 'axios';

// export default function EditNotification({ notification, onSave }) {
//   const [formData, setFormData] = useState({
//     type: '',
//     context: '',
//     recipients: [],
//     schedule: '',
//   });

//   const [users, setUsers] = useState([]);
//   const [typeOptions] = useState(['email', 'sms', 'both']); // Type options for notification type

//   useEffect(() => {
//     if (notification) {
//       setFormData(notification); // Preload the notification data for editing
//     }
//   }, [notification]);

//   useEffect(() => {
//     fetchUsers();
//   }, []);

//   const fetchUsers = async () => {
//     try {
//       const token = localStorage.getItem('authToken');
//       const response = await axios.get('http://127.0.0.1:8000/api/admin-users/', {
//         headers: {
//           Authorization: `Bearer ${token}`,
//         },
//       });
//       setUsers(response.data.results || []);
//     } catch (error) {
//       console.error('Error fetching users:', error);
//     }
//   };

//   const handleChange = (e) => {
//     const { name, value } = e.target;
//     setFormData((prev) => ({ ...prev, [name]: value }));
//   };

//   const handleRecipientsChange = (event) => {
//     const { value } = event.target;
//     if (value.includes('all')) {
//       if (formData.recipients.includes('all')) {
//         // Unselect "All Users"
//         setFormData((prev) => ({ ...prev, recipients: [] }));
//       } else {
//         // Select all users
//         setFormData((prev) => ({
//           ...prev,
//           recipients: ['all', ...users.map((user) => user.id)],
//         }));
//       }
//     } else {
//       setFormData((prev) => ({
//         ...prev,
//         recipients: value.filter((id) => id !== 'all'),
//       }));
//     }
//   };

//   const handleSubmit = () => {
//     onSave({ ...formData, id: notification.id });
//   };

//   return (
//     <div>
//       <h3>Edit Notification</h3>

//       {/* Type Field */}
//       <FormControl fullWidth margin="normal">
//         <InputLabel>Type</InputLabel>
//         <Select
//           name="type"
//           value={formData.type}
//           onChange={handleChange}
//         >
//           {typeOptions.map((type) => (
//             <MenuItem key={type} value={type}>
//               {type.toUpperCase()}
//             </MenuItem>
//           ))}
//         </Select>
//       </FormControl>

//       {/* Context Field */}
//       <TextField
//         label="Context"
//         name="context"
//         value={formData.context}
//         onChange={handleChange}
//         fullWidth
//         multiline
//         rows={4}
//         margin="normal"
//       />

//       {/* Recipients Field */}
//       <FormControl fullWidth margin="normal">
//         <InputLabel>Recipients</InputLabel>
//         <Select
//           multiple
//           value={formData.recipients}
//           onChange={handleRecipientsChange}
//           renderValue={(selected) => {
//             if (selected.includes('all')) {
//               return 'All Users';
//             }
//             return selected
//               .map((id) => {
//                 const user = users.find((u) => u.id === id);
//                 return user ? `${user.username} (${user.email || user.phone || 'N/A'})` : null;
//               })
//               .filter(Boolean)
//               .join(', ');
//           }}
//         >
//           <MenuItem value="all">
//             <Checkbox checked={formData.recipients.includes('all')} />
//             <ListItemText primary="All Users" />
//           </MenuItem>
//           {users.map((user) => (
//             <MenuItem key={user.id} value={user.id}>
//               <Checkbox checked={formData.recipients.includes(user.id)} />
//               <ListItemText primary={`${user.username} (${user.email || user.phone || 'N/A'})`} />
//             </MenuItem>
//           ))}
//         </Select>
//       </FormControl>

//       {/* Schedule Field */}
//       <TextField
//         label="Schedule"
//         name="schedule"
//         type="datetime-local"
//         value={formData.schedule}
//         onChange={handleChange}
//         fullWidth
//         margin="normal"
//         InputLabelProps={{
//           shrink: true,
//         }}
//       />

//       {/* Save Button */}
//       <Button
//         variant="contained"
//         color="primary"
//         onClick={handleSubmit}
//         style={{ marginTop: '16px' }}
//       >
//         Save Changes
//       </Button>
//     </div>
//   );
// }
